import {
  Card,
  CardBody,
  Col,
  Row,
  InputGroup,
  Input,
  Label,
  FormGroup,
  InputGroupText,
} from "reactstrap";
import Swal from "sweetalert2";
import { dynamicAPIImage } from "../../../../Utils";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { H5, H6, Image, P, Badges } from "../../../../AbstractElements";
import CommonCardHeader from "../../../CommonComponents/CommonCardHeader/CommonCardHeader";
import { Link } from "react-router-dom";
import { Camera } from "react-feather";

// Modals
import CreateEmpMovementModal from "../../../UiKits/Modal/Employee/CreateEmploymentMovement";

// Services
import {
  VIEW_EMPTIMELINE_QUERY_KEY,
  employmentHistoryService,
} from "../../../../Services/employmentHistoryServices";

// Type
import { ViewEmploymentHistory } from "../../../../Types/Employee/EmploymentHistory";
import { CRUDPermissions } from "../../../../Types/Common/Access";

interface EmployeeProfileProps {
  EmpNo: string;
  isAdmin: boolean;
  access: CRUDPermissions;
}

const EmploymentTimeline: React.FC<EmployeeProfileProps> = ({
  EmpNo,
  isAdmin,
  access,
}) => {
  const [formData, setFormData] = useState<ViewEmploymentHistory[]>([]);
  const queryClient = useQueryClient();

  const { data: responseData, isLoading: isFetching } = useQuery<
    ViewEmploymentHistory[]
  >({
    queryKey: [VIEW_EMPTIMELINE_QUERY_KEY, EmpNo],
    queryFn: () => employmentHistoryService.ViewEmploymentHistory(EmpNo),
    enabled: !!EmpNo,
  });

  useEffect(() => {
    if (responseData) {
      setFormData(responseData);
    }
  }, [responseData]);

  const toggleMutation = useMutation({
    mutationFn: employmentHistoryService.toggleStatus,
    // Show loading immediately when mutation starts
    onMutate: () => {
      Swal.fire({
        title: "Processing Request",
        text: "The system is prcoessing the transaction. Please wait...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries();
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "success",
        title: "Transaction Successful",
        text: "Employee record has been updated successfully.",
        confirmButtonColor: "#4e73df",
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.fire({
            title: "Refreshing Employee Information",
            text: "The system is refreshing the updated employee information. Please wait...",
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
              Swal.showLoading();

              setTimeout(() => {
                Swal.close();
              }, 2000);
            },
          });
        }
      });
    },
    onError: (error: any) => {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Transaction Failed",
        text:
          error.response?.data?.message ||
          "Failed to delete the record. Please reload the page and try again.",
        confirmButtonColor: "#4e73df",
      });
    },
  });

  const handleDelete = (id: number) => {
    Swal.fire({
      title: "Delete Employment History?",
      text: "This employment history record will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: '<i class="fa fa-trash"></i> Delete',
      cancelButtonText: '<i class="fa fa-times"></i> Cancel',

      confirmButtonColor: "#4e73df", // Primary
      cancelButtonColor: "#dc3545", // Danger
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        toggleMutation.mutate(id);
      }
    });
  };

  return (
    <Card className="card-absolute">
      <CommonCardHeader
        headClass="bg-primary"
        tagClass="txt-light"
        icon={<i className="icofont icofont-ui-timer me-1"></i>}
        title={"Employment Timeline"}
      />

      <CardBody>
        <Row>
          {access.update && access.create && (
            <CreateEmpMovementModal EmpNo={EmpNo} />
          )}

          <section className="cd-container" id="cd-timeline">
            {responseData?.map((item, index) => (
              <div className="cd-timeline-block" key={item.id ?? index}>
                <div
                  className={`cd-timeline-img cd-picture bg-${item.color || "info"}`}
                >
                  <i className={item.main_icon || "icon-bookmark-alt"} />
                </div>

                <div className="cd-timeline-content">
                  <div className="timeline-wrapper">
                    <Badges color={item.color || "info"}>{item.tag}</Badges>

                    <span className="ms-2">
                      {item.updated_by_user} ({item.updated_date}){" "}
                      {isAdmin && access.update && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger px-2 py-1"
                          title="Delete Employment History"
                          onClick={() => handleDelete(item.id)}
                        >
                          <i className="fa fa-trash-o" />
                        </button>
                      )}
                    </span>
                  </div>

                  <H5 className="m-0">{item.main_title}</H5>

                  <P className="mb-0">{item.description}</P>

                  {item.sub_title && (
                    <div className="time-content pt-4">
                      <i className={item.sub_icon || "icon-briefcase"} />
                      <H6>{item.sub_title}</H6>
                    </div>
                  )}

                  {item.remarks && (
                    <div className="time-content pt-4">
                      <P>{item.remarks}</P>
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <span className="cd-date">{item.effectivity_date}</span>
                  </div>
                </div>
              </div>
            ))}
          </section>
        </Row>
      </CardBody>
    </Card>
  );
};

export default EmploymentTimeline;
